import React, { useMemo } from 'react';
import {
  canonicalStatus,
  extractProductionStageDates,
  productionTimelineProgress,
  PRODUCTION_TIMELINE_STAGES,
  formatShortActivityDate,
} from './workflowConstants';
import { ProductionWorkflowGlyph } from './WorkflowGlyphs';
import {
  SECTION_CARD_HEADER,
  SECTION_CARD_HEADER_MAIN,
  SECTION_CARD_ICON,
  SECTION_CARD_TITLE_COL,
  SECTION_CARD_TITLE,
  SECTION_CARD_ACTIONS,
} from '../ui/DetailSection';

/** Linear production journey only — vendor work is in Vendor trips. */
export function OrderTimeline({ status, onHold, activities = [] }) {
  const current = canonicalStatus(status);
  const { completedThrough, activeIndex } = productionTimelineProgress(status);
  const milestoneDates = useMemo(() => extractProductionStageDates(activities), [activities]);
  const isCancelled = current === 'CANCELLED';
  const stages =
    current === 'DRAFT' || isCancelled ? [] : PRODUCTION_TIMELINE_STAGES;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <header className={SECTION_CARD_HEADER}>
        <div className={SECTION_CARD_HEADER_MAIN}>
          <span className={SECTION_CARD_ICON}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
          </span>
          <div className={SECTION_CARD_TITLE_COL}>
            <h3 className={SECTION_CARD_TITLE}>Workflow</h3>
          </div>
        </div>
        {onHold ? (
          <div className={SECTION_CARD_ACTIONS}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800 ring-1 ring-amber-200">
              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 4a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1zm9 0a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              On hold
            </span>
          </div>
        ) : null}
      </header>
      <div className="p-4 sm:p-5">
      {isCancelled ? (
        <div className="rounded-xl bg-rose-50 p-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-200">
          Order cancelled
        </div>
      ) : stages.length === 0 ? (
        <div className="rounded-xl bg-slate-50 p-3 text-sm font-semibold text-slate-600 ring-1 ring-slate-200">
          Workflow starts after approval
        </div>
      ) : (
        <div className="overflow-x-auto pb-1">
          <ol className="flex min-w-max items-start gap-0 px-1">
            {stages.map((s, i) => {
              const done = i <= completedThrough;
              const active = i === activeIndex && activeIndex > completedThrough;
              const showTick = done;
              const dateStr = milestoneDates[s.key] ? formatShortActivityDate(milestoneDates[s.key]) : '';
              const isLast = i === stages.length - 1;
              const connectorDone = i < completedThrough || (current === 'DELIVERED' && i < stages.length - 1);

              return (
                <li key={s.key} className="flex flex-1 items-start">
                  <div className="flex w-[104px] shrink-0 flex-col items-center sm:w-[118px]">
                    <span
                      className={[
                        'mb-2 text-center text-[11px] font-bold leading-tight sm:text-xs',
                        active ? 'text-primary-700' : showTick ? 'text-slate-800' : 'text-slate-400',
                      ].join(' ')}
                    >
                      {s.label}
                    </span>
                    <div className="relative">
                      <div
                        className={[
                          'flex h-14 w-14 items-center justify-center rounded-full shadow-md transition-all sm:h-16 sm:w-16',
                          showTick || active ? `${s.circleClass} text-white` : 'bg-slate-200 text-slate-500',
                          active ? 'ring-4 ring-primary-400/40' : '',
                        ].join(' ')}
                      >
                        <ProductionWorkflowGlyph stage={s.key} className="h-7 w-7 sm:h-8 sm:w-8" />
                      </div>
                      {showTick ? (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white"
                          aria-hidden
                        >
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-center text-[10px] font-medium text-slate-500 sm:text-[11px]">
                      {dateStr || '—'}
                    </p>
                  </div>
                  {!isLast ? (
                    <div
                      className={[
                        'mx-0.5 mt-[52px] h-1 flex-1 min-w-[12px] rounded-full sm:mt-[58px]',
                        connectorDone ? 'bg-emerald-500' : active ? 'bg-primary-300' : 'bg-slate-200',
                      ].join(' ')}
                      aria-hidden
                    />
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      )}
      </div>
    </section>
  );
}
